import React, { useState, useEffect, useCallback } from "react";
import {
  TrendingUp, Users, Eye, Instagram, Award, Check, X,
  Loader2, BarChart3, Star, ExternalLink, Edit2, Download
} from "lucide-react";
import CreatorLeaderboardAdmin from "./CreatorLeaderboardAdmin";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatCard = ({ icon: Icon, label, value, sub, color = "text-[#1E1B4B]" }) => (
  <div className="bg-white rounded-xl p-4 border border-gray-100 shadow-sm">
    <div className="flex items-center gap-2 mb-1">
      <Icon size={14} className="text-[#E83E8C]" />
      <span className="text-[9px] font-semibold text-[#94a3b8] uppercase tracking-wider">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
    {sub && <p className="text-[10px] text-[#94a3b8] mt-0.5">{sub}</p>}
  </div>
);

const CreatorAnalyticsTab = ({ authAxios }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get(`${API_URL}/api/creators/analytics/dashboard`);
      setData(r.data);
    } catch { /* silent */ }
    setLoading(false);
  }, [authAxios]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpdateCreator = async (creatorId) => {
    try {
      await authAxios.put(`${API_URL}/api/creators/analytics/creator/${creatorId}`, editForm);
      setEditId(null);
      setEditForm({});
      fetchData();
    } catch { /* silent */ }
  };

  const handleExport = async () => {
    try {
      const r = await authAxios.get(`${API_URL}/api/creators/analytics/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `creator_data_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
    } catch { /* silent */ }
  };

  if (loading) return <div className="text-center py-10"><Loader2 className="animate-spin text-[#E83E8C] mx-auto" size={24} /></div>;
  if (!data) return <p className="text-sm text-[#94a3b8] text-center py-10">Failed to load analytics</p>;

  const ov = data.overview;

  return (
    <div className="space-y-6" data-testid="creator-analytics">
      {/* Creator Leaderboard Admin */}
      <CreatorLeaderboardAdmin authAxios={authAxios} />

      {/* Overview Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard icon={BarChart3} label="Total Generations" value={ov.total_generations} sub={`${ov.today_generations} today`} />
        <StatCard icon={Users} label="Unique Users" value={ov.unique_users} />
        <StatCard icon={Star} label="Creators Joined" value={ov.total_creators_joined} />
        <StatCard icon={Check} label="Verified Posts" value={ov.posted_count} />
        <StatCard icon={Eye} label="Est. Reach" value={ov.estimated_reach > 1000 ? `${Math.round(ov.estimated_reach / 1000)}K` : ov.estimated_reach} />
        <StatCard icon={TrendingUp} label="Conversion" value={ov.total_creators_joined > 0 ? `${Math.round((ov.posted_count / ov.total_creators_joined) * 100)}%` : "0%"} sub="joined → posted" />
      </div>

      {/* Daily Trend */}
      {data.daily_trend?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-[#1E1B4B] mb-3">Daily Generations (Last 14 Days)</h3>
          <div className="flex items-end gap-1 h-32">
            {data.daily_trend.slice().reverse().map(d => {
              const max = Math.max(...data.daily_trend.map(x => x.count), 1);
              const height = Math.max((d.count / max) * 100, 4);
              return (
                <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.count}`}>
                  <span className="text-[8px] text-[#94a3b8]">{d.count}</span>
                  <div className="w-full rounded-t bg-gradient-to-t from-[#E83E8C] to-[#E83E8C]/50" style={{ height: `${height}%` }} />
                  <span className="text-[7px] text-[#94a3b8] truncate w-full text-center">{d.date.slice(5)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Popular Styles */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-[#1E1B4B] mb-3">Popular Styles</h3>
          <div className="space-y-2">
            {data.popular_styles?.map(s => (
              <div key={s.style} className="flex items-center justify-between">
                <span className="text-xs text-[#475569] capitalize">{s.style}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#E83E8C] rounded-full" style={{ width: `${(s.count / Math.max(ov.total_generations, 1)) * 100}%` }} />
                  </div>
                  <span className="text-[10px] text-[#94a3b8] w-8 text-right">{s.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Popular Occasions */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <h3 className="text-sm font-bold text-[#1E1B4B] mb-3">Popular Occasions</h3>
          <div className="space-y-2">
            {data.popular_occasions?.map(o => (
              <div key={o.occasion} className="flex items-center justify-between">
                <span className="text-xs text-[#475569]">{o.occasion}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${(o.count / Math.max(ov.total_generations, 1)) * 100}%` }} />
                  </div>
                  <span className="text-[10px] text-[#94a3b8] w-8 text-right">{o.count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Creator List with Tracking */}
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-[#1E1B4B]">Creators ({data.creators?.length || 0})</h3>
          <button onClick={handleExport} className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs text-[#475569] border border-gray-200 hover:bg-gray-50" data-testid="export-creators-btn">
            <Download size={12} /> Export Data
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="creators-table">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left py-2 px-2 text-[10px] text-[#94a3b8] font-semibold">Name</th>
                <th className="text-left py-2 px-2 text-[10px] text-[#94a3b8] font-semibold">Instagram</th>
                <th className="text-left py-2 px-2 text-[10px] text-[#94a3b8] font-semibold">Followers</th>
                <th className="text-left py-2 px-2 text-[10px] text-[#94a3b8] font-semibold">Posted</th>
                <th className="text-left py-2 px-2 text-[10px] text-[#94a3b8] font-semibold">Reach</th>
                <th className="text-left py-2 px-2 text-[10px] text-[#94a3b8] font-semibold">Points</th>
                <th className="text-left py-2 px-2 text-[10px] text-[#94a3b8] font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data.creators || []).map(c => (
                <tr key={c.creator_id} className="border-b border-gray-50 hover:bg-[#FAFAFA]" data-testid={`creator-row-${c.creator_id}`}>
                  <td className="py-2 px-2 font-medium text-[#1E1B4B]">{c.name}</td>
                  <td className="py-2 px-2">
                    <a href={`https://instagram.com/${c.instagram?.replace("@", "")}`} target="_blank" rel="noreferrer" className="text-[#E83E8C] hover:underline flex items-center gap-1">
                      <Instagram size={10} /> {c.instagram}
                    </a>
                  </td>
                  <td className="py-2 px-2 text-[#475569]">{c.follower_count || "-"}</td>
                  <td className="py-2 px-2">
                    {c.posted ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold"><Check size={8} /> Yes</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[9px]">No</span>
                    )}
                  </td>
                  <td className="py-2 px-2 text-[#475569]">{c.estimated_impressions ? `${c.estimated_impressions.toLocaleString()}` : "-"}</td>
                  <td className="py-2 px-2 font-bold text-[#E83E8C]">{c.points || 0}</td>
                  <td className="py-2 px-2">
                    {editId === c.creator_id ? (
                      <div className="flex items-center gap-1">
                        <input type="number" placeholder="Followers" value={editForm.follower_count || ""} onChange={e => setEditForm(f => ({ ...f, follower_count: parseInt(e.target.value) || 0 }))}
                          className="w-16 px-1 py-0.5 border rounded text-[10px]" />
                        <input type="number" placeholder="Reach" value={editForm.estimated_impressions || ""} onChange={e => setEditForm(f => ({ ...f, estimated_impressions: parseInt(e.target.value) || 0 }))}
                          className="w-16 px-1 py-0.5 border rounded text-[10px]" />
                        <input type="number" placeholder="Pts" value={editForm.points || ""} onChange={e => setEditForm(f => ({ ...f, points: parseInt(e.target.value) || 0 }))}
                          className="w-12 px-1 py-0.5 border rounded text-[10px]" />
                        <label className="flex items-center gap-0.5 text-[9px]">
                          <input type="checkbox" checked={editForm.posted || false} onChange={e => setEditForm(f => ({ ...f, posted: e.target.checked }))} /> Posted
                        </label>
                        <button onClick={() => handleUpdateCreator(c.creator_id)} className="p-1 rounded bg-emerald-500 text-white"><Check size={10} /></button>
                        <button onClick={() => { setEditId(null); setEditForm({}); }} className="p-1 rounded bg-gray-200"><X size={10} /></button>
                      </div>
                    ) : (
                      <button onClick={() => { setEditId(c.creator_id); setEditForm({ follower_count: c.follower_count, estimated_impressions: c.estimated_impressions, points: c.points, posted: c.posted }); }}
                        className="p-1 rounded hover:bg-gray-100" data-testid={`edit-creator-${c.creator_id}`}>
                        <Edit2 size={12} className="text-[#475569]" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {(!data.creators || data.creators.length === 0) && (
            <p className="text-xs text-[#94a3b8] text-center py-6">No creators have joined yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreatorAnalyticsTab;
